import type { HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, useMultiStyleConfig } from '@chakra-ui/react';
import { chakra } from '@chakra-ui/react';
import { useMemo } from 'react';

import { ConversationProvider } from '../ConversationContext';
import {
  ConversationStyles,
  ConversationStylesProvider,
} from '../useConversationStyles';
import { useStickToBottom } from '../useStickToBottom';

export interface ConversationProps extends HTMLChakraProps<'div'> {
  /**
   * Whether to start at the bottom or scroll to there initially.
   * @default true
   */
  initialAtBottom?: boolean;
  /**
   * How to handle scrolling when the container or content is resized.
   * 'smooth' uses spring animation, 'instant' jumps immediately.
   * @default 'smooth'
   */
  resize?: 'smooth' | 'instant';
  /**
   * How to handle scrolling on the first content size change.
   * 'smooth' uses spring animation, 'instant' jumps immediately.
   * @default 'instant'
   */
  initial?: 'smooth' | 'instant';
}

/**
 * Conversation is a scroll region for AI agent experiences that scrolls to bottom as new messages stream in and helps you jump to the bottom while scrolled back.
 */
export const Conversation: React.FC<ConversationProps> = forwardRef<
  ConversationProps,
  'div'
>(({ children, initialAtBottom = true, resize, initial, ...props }, ref) => {
  const promptBarStyles = useMultiStyleConfig(
    'Conversation',
  ) as ConversationStyles;

  const stickToBottom = useStickToBottom({ initialAtBottom, resize, initial });

  const conversationContext = useMemo(
    () => ({
      ...stickToBottom,
    }),
    [stickToBottom],
  );

  return (
    <ConversationStylesProvider value={promptBarStyles}>
      <ConversationProvider value={conversationContext}>
        <chakra.div
          ref={ref}
          sx={promptBarStyles.container}
          className="conversation"
          role="log"
          {...props}
        >
          {children}
        </chakra.div>
      </ConversationProvider>
    </ConversationStylesProvider>
  );
});
Conversation.displayName = 'Conversation';
